import React, { useState, createContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ITEM_SCOPE,
  PAGE_SCOPE,
  ITEM_GROUP_SCOPE,
} from '../../constants/pageModScopes';

/**
 * This context provides a wrapper that manages the page mods for the application.
 * 
 * Page mods that are specified via the site configs should be passed in to the provider as props.
 * The expected structure of a single pageUxMod is:
  {    
    modRef: {
      family,
      id,
    },
    scope,
    enabled,
  }
 * DetailsOrderUxModWrapper will not pass pageUxMods with enabled = false into its ModBoss.
 */
const PageModsContext = createContext(null);

const PageModsProvider = props => {
  const { children, siteConfigPageUxMods = [] } = props;

  const [modBossLogLevel, setModBossLogLevel] = useState('Silent');

  // Page Level Mods
  const [pageLevelPageUxMods, setPageLevelPageUxMods] = useState(
    siteConfigPageUxMods.filter(mod => mod.scope === PAGE_SCOPE)
  );

  useEffect(() => {
    setPageLevelPageUxMods(
      siteConfigPageUxMods.filter(mod => mod.scope === PAGE_SCOPE)
    );
  }, [siteConfigPageUxMods]);

  const activePageLevelPageUxMods = pageLevelPageUxMods.filter(
    mod => mod.enabled
  );

  // Item Level Mods
  const [itemLevelPageUxMods, setItemLevelPageUxMods] = useState(
    siteConfigPageUxMods.filter(mod => mod.scope === ITEM_SCOPE)
  );

  useEffect(() => {
    setItemLevelPageUxMods(
      siteConfigPageUxMods.filter(mod => mod.scope === ITEM_SCOPE)
    );
  }, [siteConfigPageUxMods]);

  const activeItemLevelPageUxMods = itemLevelPageUxMods.filter(
    mod => mod.enabled
  );

  // Item Group Level Mods
  const [itemGroupLevelPageUxMods, setItemGroupLevelPageUxMods] = useState(
    siteConfigPageUxMods.filter(mod => mod.scope === ITEM_GROUP_SCOPE)
  );

  useEffect(() => {
    setItemGroupLevelPageUxMods(
      siteConfigPageUxMods.filter(mod => mod.scop === ITEM_GROUP_SCOPE)
    );
  }, [siteConfigPageUxMods]);

  const activeItemGroupLevelPageUxMods = itemLevelPageUxMods.filter(
    mod => mod.enabled
  );

  const pageModsContextValues = useMemo(
    () => ({
      pageLevelPageUxMods,
      setPageLevelPageUxMods,
      activePageLevelPageUxMods,
      itemLevelPageUxMods,
      setItemLevelPageUxMods,
      activeItemLevelPageUxMods,
      itemGroupLevelPageUxMods,
      setItemGroupLevelPageUxMods,
      activeItemGroupLevelPageUxMods,
      modBossLogLevel,
      setModBossLogLevel,
    }),
    [
      pageLevelPageUxMods,
      setPageLevelPageUxMods,
      activePageLevelPageUxMods,
      itemLevelPageUxMods,
      setItemLevelPageUxMods,
      activeItemLevelPageUxMods,
      itemGroupLevelPageUxMods,
      setItemGroupLevelPageUxMods,
      activeItemGroupLevelPageUxMods,
      modBossLogLevel,
      setModBossLogLevel,
    ]
  );

  return (
    <PageModsContext.Provider value={pageModsContextValues}>
      {children}
    </PageModsContext.Provider>
  );
};

PageModsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  siteConfigPageUxMods: PropTypes.array,
};

export { PageModsContext, PageModsProvider };
